import {
    Amount,
    AuthContext,
    BaseButton,
    CollectionPaymentSourceType, CoreFetch, formatDate, IAuthContextProps,
    ProviderCollectionPayment, ProviderSubscriberPatient,
    renderAmount,
    SupportCollectionPayment, TerminalApi, TerminalTransactionResponse
} from '@enter/core';
import _ from 'lodash';
import {inject, observer} from 'mobx-react';
import React, {useContext} from 'react';
import {DialogContext} from 'services';
import {RootStore} from 'stores';
import {CollectionRefundDialog} from '../collection-refund-dialog';
import * as S from './claim-timeline-table.styles';

interface ICollectionDetailProps {
    collection: ProviderCollectionPayment;
    subscriberPatients?: ProviderSubscriberPatient[];
}

// Reusable row components
const TextRow = React.memo<{
    label?: string;
    value: string | number | undefined;
}>(({ label, value }) => {
    if (!value) {
        return null;
    }

    return (
        <S.ExpandTR>
            {label && <S.ExpandTH>{label}</S.ExpandTH>}
            <S.ExpandTD>{value}</S.ExpandTD>
        </S.ExpandTR>
    );
});

const DateRow = React.memo<{
    label?: string;
    value: string | undefined;
}>(({ label, value }) => {
    if (!value) {
        return null;
    }

    const date = formatDate(value, 'MMM dd, yyyy');

    return (
        <S.ExpandTR>
            {label && <S.ExpandTH>{label}</S.ExpandTH>}
            <S.ExpandTD>{date}</S.ExpandTD>
        </S.ExpandTR>
    );
});

const AmountRow = React.memo<{
    label?: string;
    value: Amount | undefined;
}>(({ label, value }) => {
    if (!value) {
        return null;
    }

    return (
        <S.ExpandTR>
            {label && <S.ExpandTH>{label}</S.ExpandTH>}
            <S.ExpandTD>{renderAmount(value)}</S.ExpandTD>
        </S.ExpandTR>
    );
});

const BooleanRow = React.memo<{
    label?: string;
    value: boolean | undefined;
}>(({ label, value }) => {
    if (value === undefined) {
        return null;
    }

    return (
        <S.ExpandTR>
            {label && <S.ExpandTH>{label}</S.ExpandTH>}
            <S.ExpandTD>{value ? 'Yes' : 'No'}</S.ExpandTD>
        </S.ExpandTR>
    );
});

async function launchTerminalRefund(payment: SupportCollectionPayment, subscriberPatients?: ProviderSubscriberPatient[], context?: IAuthContextProps) {
    console.log("launchTerminalRefund with payment=" + payment.id);

    let externalPatientId: string | undefined = undefined;
    let terminalTransactionId: string | undefined = undefined;

    if(subscriberPatients) {
        for (const subscriberPatient of subscriberPatients) {
            if (subscriberPatient.patient && subscriberPatient.patient.externalId) {
                externalPatientId = subscriberPatient.patient.externalId;
            }
        }
    }

    const launchTerminal = () => {
        const launchPayload = {
            token: context.getCurrentAccessToken(),
            refreshToken: context.getCurrentRefreshToken(),
            mode: 'refund',
            patient: externalPatientId,
            refund: terminalTransactionId
        };

        console.log("Launching with payload: ", launchPayload);

        (window as any).Terminal.launch(launchPayload);
    }

    const launchTerminalRefundPopup = () => {
        const isSandbox = /sandbox/.test(process.env.REACT_APP_API_URL || '');
        const terminalLink = isSandbox ? 'https://terminal.sandbox.enter.health' : 'https://terminal.enter.health';

        const terminalScript = document.getElementById('enter-payments-script');

        if(!terminalScript) {
            const script = document.createElement("script");
            script.id = "enter-payments-script";
            script.src = terminalLink + '/terminal.min.js';
            script.async = true;

            script.onload = () => {
                setTimeout(() => {
                    launchTerminal();
                }, 500);
            };

            document.body.appendChild(script);
        } else {
            launchTerminal();
        }
    }

    if(externalPatientId) {
        const terminalTransactionResponse: TerminalTransactionResponse = await CoreFetch<TerminalApi>(TerminalApi).getTerminalTransactions({patientId: externalPatientId, collectionPaymentId: payment.id});
        if(terminalTransactionResponse && terminalTransactionResponse.transactions && terminalTransactionResponse.transactions.length > 0) {
            terminalTransactionId = terminalTransactionResponse.transactions[0].transactionId;
            launchTerminalRefundPopup();
        }
    }


}

export const CollectionDetail: React.FunctionComponent<ICollectionDetailProps> = inject('rootStore')(
    observer(
        (props: ICollectionDetailProps): React.ReactElement => {
            const rootStore: RootStore = _.get(props, 'rootStore');
            const context = useContext(AuthContext);

            if (!rootStore) {
                return <></>;
            }

            const [buttonWaiting, setButtonWaiting] = React.useState(false);

            const dialogContext = useContext(DialogContext);

            const { collection, subscriberPatients } = props;

            const handleCloseDialog = () => {
                dialogContext.hideDialog();
            };

            const handleRefund = async () => {
                const handleSubmit = () => {
                    handleCloseDialog();
                };

                if(collection.source === CollectionPaymentSourceType.TERMINAL) {
                    setButtonWaiting(true);
                    await launchTerminalRefund(collection, subscriberPatients, context);
                    setButtonWaiting(false);
                    return;
                }

                dialogContext.showDialog({
                    props: {
                        onClose: handleCloseDialog,
                        disableBackdropClick: true,
                        disableEscapeKeyDown: true,
                        renderTitle: 'Refund Payment',
                    },
                    Component: (
                        <CollectionRefundDialog
                            onClose={handleCloseDialog}
                            paymentId={collection.id!}
                            onSubmit={handleSubmit}
                        />
                    ),
                });
            };

            return (
                <S.ExpandWrap key={collection.id}>
                    <S.ExpandContainer>
                        <S.ExpandTable>
                            <S.ExpandTBody>
                                <TextRow label="ID" value={collection.id} />
                                <AmountRow label="Collected Amount" value={collection.collectedAmount} />
                                <TextRow label="Source" value={collection.source} />
                                <DateRow label="Payment Date" value={collection.paymentDate} />
                                <BooleanRow label="Manual" value={collection.manual} />
                                <TextRow label="Memo" value={collection.memo} />
                                <AmountRow label="Refunded Amount" value={collection.refundedAmount} />
                            </S.ExpandTBody>
                        </S.ExpandTable>
                    </S.ExpandContainer>
                </S.ExpandWrap>
            );
        },
    ),
);
